<script setup lang="ts">
import { computed } from 'vue'

import type { BetaLinkBuildingLink } from '@/types/beta-dashboard'

const props = defineProps<{
    link: BetaLinkBuildingLink
    deleting?: boolean
}>()

const emit = defineEmits<{
    edit: [link: BetaLinkBuildingLink]
    remove: [link: BetaLinkBuildingLink]
}>()

const destination = computed(() => (
    props.link.typeRoute === 'whats'
        ? `WhatsApp · ${props.link.phone || 'sem número'}`
        : props.link.route_path || 'URL não informada'
))
</script>

<template>
    <article class="beta-link-building-card">
        <button type="button" class="beta-link-building-card__drag" aria-label="Reordenar link">
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M5 7h14M5 12h14M5 17h14" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" />
            </svg>
        </button>

        <div class="beta-link-building-card__copy">
            <strong>{{ link.route_name || 'Link sem nome' }}</strong>
            <small>{{ destination }}</small>
        </div>

        <span v-if="link.target_blank" class="beta-link-building-card__target">Nova aba</span>

        <div class="beta-link-building-card__actions">
            <button type="button" class="is-edit" @click="emit('edit', link)">Editar</button>
            <button type="button" class="is-remove" :disabled="deleting" @click="emit('remove', link)">
                {{ deleting ? 'Excluindo…' : 'Excluir' }}
            </button>
        </div>
    </article>
</template>

<style scoped>
.beta-link-building-card {
    display: grid;
    min-width: 0;
    grid-template-columns: 32px minmax(0, 1fr) auto auto;
    align-items: center;
    gap: 10px;
    border: 1px solid #e2e8f0;
    border-radius: 14px;
    background: #fff;
    padding: 10px 12px;
}

.beta-link-building-card__drag {
    display: grid;
    width: 32px;
    height: 32px;
    place-items: center;
    color: #8190a4;
    cursor: grab;
}

.beta-link-building-card__drag:active {
    cursor: grabbing;
}

.beta-link-building-card__drag svg {
    width: 19px;
    height: 19px;
}

.beta-link-building-card__copy {
    min-width: 0;
}

.beta-link-building-card__copy strong,
.beta-link-building-card__copy small {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.beta-link-building-card__copy strong {
    color: #263d5a;
    font-size: 12px;
    font-weight: 850;
}

.beta-link-building-card__copy small {
    margin-top: 3px;
    color: #8493a7;
    font-size: 10px;
}

.beta-link-building-card__target {
    border-radius: 999px;
    background: #f0f4f8;
    padding: 5px 8px;
    color: #718299;
    font-size: 10px;
    font-weight: 800;
}

.beta-link-building-card__actions {
    display: flex;
    gap: 9px;
}

.beta-link-building-card__actions button {
    font-size: 9px;
    font-weight: 850;
}

.beta-link-building-card__actions .is-edit {
    color: var(--main-color);
}

.beta-link-building-card__actions .is-remove {
    color: #b65353;
}

.beta-link-building-card__actions button:disabled {
    cursor: wait;
    opacity: 0.55;
}

@media (max-width: 600px) {
    .beta-link-building-card {
        grid-template-columns: 28px minmax(0, 1fr) auto;
    }

    .beta-link-building-card__target {
        display: none;
    }

    .beta-link-building-card__actions {
        grid-column: 2 / -1;
        justify-content: flex-end;
    }
}
</style>
